/**
 * Mode « défilement » : la leçon en cartes plein écran que l'élève fait glisser vers le haut,
 * une idée par carte. Mêmes blocs que le mode « pas à pas » (BlockView) : rien à rédiger deux fois.
 *
 * Sobriété et petits téléphones :
 *  - une carte à la fois (scroll-snap, arrêt obligatoire sur chaque carte) ;
 *  - seules la carte visible et ses deux voisines sont réellement rendues (jamais plus d'une 3D active) ;
 *  - les figures et la 3D sont protégées : le glissement fait défiler, « Manipuler » libère le geste ;
 *  - fil borné, carte de fin : jamais de défilement infini ni d'enchaînement automatique.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LessonBlock, LessonStep } from "@jange/shared";
import { BlockView, Formula, md, PLAYER_CSS, useLessonParams, type BlockContext, type PlayerEvent, type ThreeComponents } from "./blocks.js";
import { C } from "./theme.js";

export type FeedCard = {
  key: string;
  step: LessonStep;
  blocks: (LessonBlock & { id: string })[];
  /** Révision : notion d'origine de la carte et clé de répétition espacée. */
  notionId?: string;
  notionTitle?: string;
  reviewKey?: string;
};

export type CardFeedProps = {
  cards: FeedCard[];
  variant: "lesson" | "review";
  title: string;
  lang: string;
  onEvent: (e: PlayerEvent) => Promise<void> | void;
  resolveAsset?: (assetId: string) => string | null;
  assetUrls?: Record<string, string>;
  autoLoad3d?: boolean;
  three?: ThreeComponents;
};

const FEED_CSS = `
.jg-feed { scrollbar-width: none; }
.jg-feed::-webkit-scrollbar { display: none; }
.jg-card { scroll-snap-align: start; scroll-snap-stop: always; }
@keyframes jg-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.jg-card.is-current .jg-rise { animation: jg-rise 320ms cubic-bezier(.2,.8,.2,1) both; }
.jg-card.is-current .jg-rise:nth-child(2) { animation-delay: 60ms; }
.jg-card.is-current .jg-rise:nth-child(3) { animation-delay: 120ms; }
@media (prefers-reduced-motion: reduce) { .jg-card.is-current .jg-rise { animation: none; } }
`;

const pill = (active = false) => ({
  border: "none", borderRadius: 999, padding: "8px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer",
  background: active ? C.teal : "rgba(255,255,255,0.92)", color: active ? "#fff" : C.ink,
  boxShadow: "0 1px 6px rgba(21,33,59,0.14)",
}) as const;

/** Loin de l'écran, la 3D est remplacée par un cadre de même taille : un seul contexte WebGL actif. */
const Far3D = () => <div style={{ height: 380, borderRadius: 18, border: `1px solid ${C.border}`, background: "#F6F7F9" }} />;
const FAR_THREE: ThreeComponents = { Solid3D: Far3D, Model3D: Far3D };

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const readMuted = () => { try { return localStorage.getItem("jange.feed.muted") === "1"; } catch { return false; } };

export function CardFeed({ cards, variant, title, lang, onEvent, resolveAsset: resolveProp, assetUrls, autoLoad3d = false, three }: CardFeedProps) {
  const resolveAsset = useCallback((id: string) => assetUrls?.[id] ?? resolveProp?.(id) ?? null, [assetUrls, resolveProp]);
  const total = cards.length + 1; // + carte de fin
  const box = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);
  const [locked, setLocked] = useState<string | null>(null); // « cardKey:blockId » en cours de manipulation
  const [muted, setMuted] = useState(readMuted);
  const [scrolled, setScrolled] = useState(false);
  const [results, setResults] = useState<Record<string, boolean>>({}); // révision : clé → réussite
  // Une carte déjà affichée reste montée (pas de saut de défilement) ; seule la 3D est démontée au loin.
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0, 1]));
  useEffect(() => { setSeen((s) => (s.has(current) && s.has(current + 1) ? s : new Set([...s, current, current + 1]))); }, [current]);
  const emit = useRef(onEvent);
  emit.current = onEvent;

  // Temps passé par carte : envoyé quand l'élève quitte la carte (ou la page).
  const shownAt = useRef({ index: 0, at: Date.now() });
  const flushDwell = useCallback((index: number) => {
    const card = cards[index];
    if (!card) return;
    void emit.current({ type: "CARD_VIEWED", stepId: card.step.id, index, dwellMs: Math.max(0, Date.now() - shownAt.current.at) });
  }, [cards]);

  useEffect(() => {
    const root = box.current;
    if (!root) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting && e.intersectionRatio >= 0.55) {
        const i = Number((e.target as HTMLElement).dataset.index);
        setCurrent((prev) => {
          if (prev === i) return prev;
          flushDwell(prev);
          shownAt.current = { index: i, at: Date.now() };
          return i;
        });
      }
    }, { root, threshold: [0.55] });
    root.querySelectorAll<HTMLElement>("[data-index]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [cards.length, flushDwell]);

  // Quitter la leçon (fermeture, retour) : on envoie aussi la carte en cours.
  useEffect(() => {
    const leave = () => flushDwell(shownAt.current.index);
    window.addEventListener("pagehide", leave);
    return () => { window.removeEventListener("pagehide", leave); leave(); };
  }, [flushDwell]);

  const completedSent = useRef(false);
  useEffect(() => {
    if (current === cards.length && !completedSent.current && variant === "lesson") {
      completedSent.current = true;
      void emit.current({ type: "LESSON_COMPLETED" });
    }
  }, [current, cards.length, variant]);

  const goTo = (i: number) => {
    const el = box.current?.querySelector<HTMLElement>(`[data-index="${i}"]`);
    el?.scrollIntoView({ behavior: reduced() ? "auto" : "smooth", block: "start" });
  };
  const toggleMute = () => setMuted((m) => { try { localStorage.setItem("jange.feed.muted", m ? "0" : "1"); } catch { /* rien */ } return !m; });
  const hasNarration = cards.some((c) => c.step.audio);

  const answer = (card: FeedCard, correct: boolean) => {
    if (!card.reviewKey || !card.notionId || results[card.reviewKey] !== undefined) return;
    setResults((r) => ({ ...r, [card.reviewKey!]: correct }));
    void emit.current({ type: "REVIEW_ANSWERED", cardKey: card.reviewKey, notionId: card.notionId, correct });
  };

  return (
    <div className="jg-player" style={{ position: "relative", height: "100%", minHeight: 0, fontFamily: "system-ui, -apple-system, sans-serif", color: C.ink, background: "#fff" }}>
      <style>{PLAYER_CSS + FEED_CSS}</style>

      {/* Progression : une barre par carte (fine au-delà de 24 cartes) */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 3, padding: "10px 14px 8px", background: "linear-gradient(#fff 70%, rgba(255,255,255,0))", pointerEvents: "none" }}>
        <div style={{ display: "flex", gap: total > 24 ? 0 : 4 }}>
          {total > 24
            ? <div style={{ height: 4, flex: 1, borderRadius: 999, background: C.border, overflow: "hidden" }}><div style={{ height: "100%", width: "100%", background: C.teal, transformOrigin: "left", transform: `scaleX(${(current + 1) / total})`, transition: "transform 250ms ease" }} /></div>
            : Array.from({ length: total }, (_, i) => <div key={i} style={{ height: 4, flex: 1, borderRadius: 999, background: i <= current ? C.teal : C.border, transition: "background-color 250ms ease" }} />)}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 12, fontWeight: 700, color: C.muted }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{title}</span>
          <span>{Math.min(current + 1, total)} / {total}</span>
        </div>
      </div>
      {hasNarration && (
        <button onClick={toggleMute} aria-label={muted ? "Activer la voix" : "Couper la voix"} style={{ ...pill(!muted), position: "absolute", top: 52, right: 12, zIndex: 4 }}>
          {muted ? "🔇 Voix" : "🔊 Voix"}
        </button>
      )}

      <div ref={box} className="jg-feed" onScroll={() => !scrolled && setScrolled(true)}
        style={{ height: "100%", overflowY: locked ? "hidden" : "auto", scrollSnapType: "y mandatory", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}>
        {cards.map((card, i) => (
          <Card key={card.key} card={card} index={i} total={total} near={Math.abs(i - current) <= 1} mounted={seen.has(i) || Math.abs(i - current) <= 1} isCurrent={i === current}
            variant={variant} lang={lang} three={three} resolveAsset={resolveAsset} autoLoad3d={autoLoad3d} muted={muted}
            locked={locked} setLocked={setLocked} result={card.reviewKey ? results[card.reviewKey] : undefined}
            onEvent={(e) => {
              if (e.type === "QUICK_CHECK" && variant === "review") { answer(card, e.correct); return; }
              void emit.current(e);
            }}
            onSelfAssess={(ok) => answer(card, ok)}
            onNext={() => goTo(i + 1)}
            hint={i === 0 && !scrolled} />
        ))}
        <EndCard index={cards.length} variant={variant} cards={cards} results={results} lang={lang}
          onRestart={() => goTo(0)} onPractice={() => void emit.current({ type: "PRACTICE_REQUESTED" })} />
      </div>
    </div>
  );
}

function Card({ card, index, total, near, mounted, isCurrent, variant, lang, three, resolveAsset, autoLoad3d, muted, locked, setLocked, result, onEvent, onSelfAssess, onNext, hint }: {
  card: FeedCard; index: number; total: number; near: boolean; mounted: boolean; isCurrent: boolean; variant: "lesson" | "review"; lang: string;
  three?: ThreeComponents; resolveAsset: (id: string) => string | null; autoLoad3d: boolean; muted: boolean;
  locked: string | null; setLocked: (k: string | null) => void; result: boolean | undefined;
  onEvent: (e: PlayerEvent) => void; onSelfAssess: (ok: boolean) => void; onNext: () => void; hint: boolean;
}) {
  const [helpSent, setHelpSent] = useState(false);
  const isQuizCard = card.blocks.length > 0 && card.blocks.every((b) => b.type === "quiz");

  return (
    <section data-index={index} className={`jg-card${isCurrent ? " is-current" : ""}`}
      style={{ minHeight: "100%", boxSizing: "border-box", padding: "74px 16px 28px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 14, borderBottom: `1px solid ${C.border}` }}>
      {mounted
        ? <CardBody card={card} lang={lang} three={near ? three : FAR_THREE} resolveAsset={resolveAsset} autoLoad3d={autoLoad3d} locked={locked} setLocked={setLocked} onEvent={onEvent} isCurrent={isCurrent} muted={muted} />
        : <div style={{ height: 200 }} aria-hidden />}

      {variant === "review" && card.reviewKey && !isQuizCard && (
        result === undefined ? (
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => onSelfAssess(false)} style={{ flex: 1, minHeight: 48, borderRadius: 14, border: `2px solid ${C.amber}`, background: "#fff", color: C.amberText, fontSize: 15, fontWeight: 700 }}>À revoir</button>
            <button onClick={() => onSelfAssess(true)} style={{ flex: 1, minHeight: 48, borderRadius: 14, border: "none", background: C.teal, color: "#fff", fontSize: 15, fontWeight: 700 }}>Je savais ✓</button>
          </div>
        ) : <div className="jg-in" style={{ fontSize: 14, fontWeight: 700, color: result ? C.teal : C.amberText }}>{result ? "Noté : elle reviendra plus tard." : "Noté : elle reviendra demain."}</div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: "auto", paddingTop: 8 }}>
        {variant === "lesson" ? (
          <button disabled={helpSent} onClick={() => { setHelpSent(true); onEvent({ type: "HELP_REQUESTED", stepId: card.step.id }); }}
            style={{ border: `1px solid ${C.border}`, background: "#fff", borderRadius: 999, padding: "8px 12px", fontSize: 13, fontWeight: 600, color: helpSent ? C.teal : C.muted }}>
            {helpSent ? "Noté : ton enseignant le saura" : "Je n'ai pas compris"}
          </button>
        ) : <span />}
        {hint
          ? <span style={{ fontSize: 13, fontWeight: 700, color: C.muted }}>Glisse vers le haut ↑</span>
          : (index < total - 1 && (variant === "review" ? result !== undefined : false)) && <button onClick={onNext} style={pill(true)}>Suivante ↓</button>}
      </div>
    </section>
  );
}

function CardBody({ card, lang, three, resolveAsset, autoLoad3d, locked, setLocked, onEvent, isCurrent, muted }: {
  card: FeedCard; lang: string; three?: ThreeComponents; resolveAsset: (id: string) => string | null; autoLoad3d: boolean;
  locked: string | null; setLocked: (k: string | null) => void; onEvent: (e: PlayerEvent) => void; isCurrent: boolean; muted: boolean;
}) {
  // Chaque carte a ses propres valeurs de curseurs : en arrivant sur la carte, la figure glisse vers celles de l'étape.
  const { params, setParams, specs, solids } = useLessonParams(card.blocks, card.step.figureParams);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [needsTap, setNeedsTap] = useState(false);
  const narration = card.step.audio ? resolveAsset(card.step.audio.assetId) : null;

  // Narration : démarre quand la carte est visible (si la voix n'est pas coupée), s'arrête quand on la quitte.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !narration) return;
    if (isCurrent && !muted) {
      a.currentTime = card.step.audio?.startSec ?? 0;
      a.play().then(() => setNeedsTap(false)).catch(() => setNeedsTap(true)); // lecture auto refusée : bouton
    } else a.pause();
  }, [isCurrent, muted, narration, card.step.audio?.startSec]);

  const guard = useCallback((blockId: string, node: ReactNode) => {
    const k = `${card.key}:${blockId}`;
    const active = locked === k;
    return (
      <div style={{ position: "relative" }}>
        {node}
        {!active && (
          // Couche transparente : le doigt fait défiler les cartes au lieu de bouger la figure.
          <div style={{ position: "absolute", inset: 0, borderRadius: 18, display: "flex", alignItems: "flex-end", justifyContent: "flex-end", padding: 10 }}>
            <button onClick={() => setLocked(k)} style={pill(true)}>✋ Manipuler</button>
          </div>
        )}
        {active && (
          <button onClick={() => setLocked(null)} style={{ ...pill(true), position: "absolute", top: 10, right: 10 }}>Terminé</button>
        )}
      </div>
    );
  }, [card.key, locked, setLocked]);

  const ctx: BlockContext = useMemo(() => ({
    lang, params, specs, solids, three, resolveAsset, autoLoad3d, compact: true, guard,
    setParam: (name, v) => setParams((p) => ({ ...p, [name]: v })),
    onEvent,
  }), [lang, params, specs, solids, three, resolveAsset, autoLoad3d, guard, setParams, onEvent]);

  const title = card.step.title[lang] ?? card.step.title.fr ?? "";
  return (
    <>
      {card.notionTitle && <span className="jg-rise" style={{ alignSelf: "flex-start", fontSize: 12, fontWeight: 700, color: C.muted, background: "#F1F3F6", padding: "4px 10px", borderRadius: 999 }}>{card.notionTitle}</span>}
      {title && <h2 className="jg-rise" style={{ margin: 0, fontSize: 24, lineHeight: 1.2, fontWeight: 750, letterSpacing: "-0.01em" }}>{title}</h2>}
      {card.blocks.map((b) => <div key={b.id} className="jg-rise"><BlockView block={b} ctx={ctx} animKey={`${card.key}-${b.id}`} /></div>)}
      {narration && (
        <>
          <audio ref={audioRef} src={narration} preload="none" />
          {needsTap && !muted && <button onClick={() => { void audioRef.current?.play(); setNeedsTap(false); }} style={{ ...pill(true), alignSelf: "flex-start" }}>▶ Écouter l'explication</button>}
        </>
      )}
    </>
  );
}

function EndCard({ index, variant, cards, results, onRestart, onPractice }: {
  index: number; variant: "lesson" | "review"; cards: FeedCard[]; results: Record<string, boolean>; lang: string; onRestart: () => void; onPractice: () => void;
}) {
  const recap = cards.filter((c) => c.step.recap).flatMap((c) => c.blocks.filter((b) => b.type === "text" || b.type === "mistake" || b.type === "formula").map((b) => ({ key: `${c.key}-${b.id}`, b })));
  const answered = Object.values(results);
  const right = answered.filter(Boolean).length;
  return (
    <section data-index={index} className="jg-card" style={{ minHeight: "100%", boxSizing: "border-box", padding: "74px 16px 28px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 16 }}>
      <div style={{ width: 56, height: 56, borderRadius: 999, background: C.tealTint, color: C.teal, display: "grid", placeItems: "center", fontSize: 28, fontWeight: 800 }}>✓</div>
      <h2 style={{ margin: 0, fontSize: 26, fontWeight: 750 }}>{variant === "lesson" ? "Leçon terminée" : "Révision terminée"}</h2>
      {variant === "review"
        ? <p style={{ margin: 0, fontSize: 16, color: C.muted }}>{answered.length ? `${right} sur ${answered.length} réussies. Les cartes ratées reviendront demain.` : "Reviens demain pour la suite."}</p>
        : recap.length > 0 && (
          <div style={{ background: C.tealTint, borderRadius: 16, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <b style={{ fontSize: 13, color: C.teal, textTransform: "uppercase", letterSpacing: "0.04em" }}>À retenir</b>
            {recap.map(({ key, b }) => b.type === "formula"
              ? <Formula key={key} latex={b.latex} display={b.display} />
              : <p key={key} style={{ margin: 0, fontSize: 15, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: md((b as { md: string }).md) }} />)}
          </div>
        )}
      {variant === "lesson" && <button onClick={onPractice} style={{ minHeight: 52, borderRadius: 14, border: "none", background: C.teal, color: "#fff", fontSize: 16, fontWeight: 700 }}>Je m'entraîne</button>}
      <button onClick={onRestart} style={{ minHeight: 48, borderRadius: 14, border: `1px solid ${C.border}`, background: "#fff", color: C.ink, fontSize: 15, fontWeight: 600 }}>{variant === "lesson" ? "Revoir depuis le début" : "Revoir les cartes"}</button>
    </section>
  );
}

/** Une leçon en cartes : une étape = une carte. */
export function lessonCards(lesson: { steps: LessonStep[]; blocks: (LessonBlock & { id: string })[] }): FeedCard[] {
  const byId = new Map(lesson.blocks.map((b) => [b.id, b]));
  return lesson.steps.map((step) => ({ key: step.id, step, blocks: step.blockIds.map((id) => byId.get(id)).filter((b): b is LessonBlock & { id: string } => !!b) }));
}
